import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, Printer, Search, Trash2, Edit2, 
  Smartphone, User, Phone, Calendar, FileText,
  Lock, AlertCircle, X, Save, Eye, Settings, Package, Receipt, History, Wifi, WifiOff,
  Archive, CheckCircle, Wrench, Truck, Mail, MessageSquare, Send, Bell, MessageCircle, Tag, Euro, Gift,
  XCircle, ChevronDown, AlertTriangle, Star
} from 'lucide-react';
import PatternLockInput, { generatePatternSVG } from '@/components/PatternLockInput';
import { printDepositReceiptViaQz, printDepositLabelViaQz, printCreditNoteViaQz, getQzStatus } from '@/utils/qzTrayService';
import ReviewRequestModal from '@/components/ReviewRequestModal';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Configuration imprimante par défaut
const DEFAULT_PRINTER_CONFIG = {
  shopName: 'PHONELAB',
  address: '5 Rue Paul Verlaine',
  city: '69100 VILLEURBANNE',
  phone: '04 26 00 82 10',
  ticketWidth: 80,
  fontSize: 14,
  useQzTray: true,
  printerName: ''
};

export default function DepositReceiptPage() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [existingClients, setExistingClients] = useState([]); // Fiches clients existantes pour auto-détection
  const [clientSuggestions, setClientSuggestions] = useState([]); // Suggestions filtrées en temps réel
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [showArchives, setShowArchives] = useState(false); // Toggle pour afficher les archives
  const [showNotifyModal, setShowNotifyModal] = useState(null); // Pour le modal de notification
  const [notifyChannels, setNotifyChannels] = useState({ email: false, sms: false, whatsapp: false });
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyPhone, setNotifyPhone] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(null); // Pour le sous-menu de statuts
  const [showReviewModal, setShowReviewModal] = useState(null); // Modal demande d'avis
  
  // État pour le choix d'impression
  const [showPrintChoice, setShowPrintChoice] = useState(null);
  const [printingInProgress, setPrintingInProgress] = useState(false);
  
  // Printer configuration state - Utilise la MÊME config que le ticket de caisse
  const [printerConfig, setPrinterConfig] = useState(() => {
    // D'abord essayer la config partagée (printerConfig)
    const sharedConfig = localStorage.getItem('printerConfig');
    if (sharedConfig) {
      return JSON.parse(sharedConfig);
    }
    // Sinon config par défaut
    return DEFAULT_PRINTER_CONFIG;
  });
  
  // QZ Tray state
  const [qzStatus, setQzStatus] = useState({ available: false, connected: false, printers: [] });
  const [availablePrinters, setAvailablePrinters] = useState([]);
  
  // Credit Note (Bon d'avoir) state
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteData, setCreditNoteData] = useState({
    receipt: null, // null = bon d'avoir libre
    clientName: '',
    clientPhone: '',
    amount: '',
    reason: '',
    validity: '12 mois'
  });
  
  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    deviceName: '',
    deviceBrand: '',
    deviceModel: '',
    problem: '',
    lockCode: '',
    lockPattern: '', // Pour le dessin du pattern
    observations: '',
    estimatedPrice: '',
    estimatedDate: '',
    isPaid: false,
    sendNotification: false, // Désactivé par défaut (SMS facturés)
    notificationChannels: [] // Aucune notification automatique
  });

  const token = localStorage.getItem('token');

  // Vérifier le statut de QZ Tray au chargement
  useEffect(() => {
    const checkQzTray = async () => {
      const status = await getQzStatus();
      setQzStatus(status);
      if (status.connected) {
        setAvailablePrinters(status.printers);
      }
    };
    const timer = setTimeout(checkQzTray, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchReceipts();
    fetchExistingClients();
  }, []);

  const fetchExistingClients = async () => {
    try {
      const response = await axios.get(`${API}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clientsList = response.data.items || response.data || [];
      setExistingClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Recherche client existant à partir du nom, téléphone ou email tapé
  const searchExistingClients = (query, field = 'all') => {
    if (!query || query.trim().length < 2) {
      setClientSuggestions([]);
      setShowClientSuggestions(false);
      return;
    }
    const q = query.toLowerCase().trim();
    // Extraire aussi les clients depuis les bons d'entrée existants (source secondaire)
    const receiptClients = receipts.map(r => ({
      id: `receipt-${r.id}`,
      name: r.clientName,
      phone: r.clientPhone,
      email: r.clientEmail,
      _source: 'receipt'
    })).filter(c => c.name || c.phone || c.email);

    const all = [...existingClients, ...receiptClients];

    // Dédupliquer par téléphone ou par nom+email
    const seen = new Set();
    const deduped = [];
    for (const c of all) {
      const key = (c.phone || '') + '|' + (c.name || '').toLowerCase() + '|' + (c.email || '').toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(c);
      }
    }

    const results = deduped.filter(c => {
      const nameMatch = c.name?.toLowerCase().includes(q);
      const phoneMatch = c.phone?.replace(/\s+/g, '').includes(q.replace(/\s+/g, ''));
      const emailMatch = c.email?.toLowerCase().includes(q);
      if (field === 'name') return nameMatch;
      if (field === 'phone') return phoneMatch;
      if (field === 'email') return emailMatch;
      return nameMatch || phoneMatch || emailMatch;
    }).slice(0, 8);

    setClientSuggestions(results);
    setShowClientSuggestions(results.length > 0);
  };

  const selectExistingClient = (client) => {
    setFormData({
      ...formData,
      clientName: client.name || '',
      clientPhone: client.phone || '',
      clientEmail: client.email || ''
    });
    setShowClientSuggestions(false);
    setClientSuggestions([]);
    toast.success('Fiche client existante appliquée');
  };

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(`${API}/api/deposit-receipts`, {
        params: { limit: 100000 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data.items || response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateTicketNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PL${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clientName || !formData.deviceName || !formData.problem) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      const receiptData = {
        ...formData,
        ticketNumber: editingReceipt?.ticketNumber || generateTicketNumber(),
      };

      if (editingReceipt) {
        await axios.put(`${API}/api/deposit-receipts/${editingReceipt.id}`, receiptData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bon modifié avec succès');
      } else {
        const response = await axios.post(`${API}/api/deposit-receipts`, receiptData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bon créé avec succès');
        
        // Afficher le résultat de la notification si envoyée
        if (response.data.notification) {
          const notif = response.data.notification;
          if (notif.results?.email?.success) {
            toast.success(`Email envoyé à ${notif.results.email.sent_to}`);
          }
          if (notif.results?.sms?.success) {
            toast.success(`SMS envoyé à ${notif.results.sms.sent_to}`);
          }
        }
      }

      setShowModal(false);
      resetForm();
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce bon d\'entrée ?')) return;
    
    try {
      await axios.delete(`${API}/api/deposit-receipts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bon supprimé');
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateReceiptStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/api/deposit-receipts/${id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const statusLabels = {
        pending: 'En attente',
        in_progress: 'En cours',
        repaired: 'Réparé',
        delivered: 'Livré',
        not_repairable: 'Non réparable',
        cancelled: 'Annulé',
        hs: 'HS'
      };
      toast.success(`Statut mis à jour: ${statusLabels[newStatus]}`);
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Désarchiver un bon (le remettre en statut "en cours")
  const unarchiveReceipt = async (id) => {
    try {
      await axios.patch(`${API}/api/deposit-receipts/${id}/status`, 
        { status: 'in_progress' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bon désarchivé - Statut remis en "En cours"');
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors du désarchivage');
    }
  };

  // Ouvrir le modal de demande d'avis
  const openReviewModal = (receipt) => {
    setShowReviewModal({
      id: receipt.id,
      name: receipt.clientName,
      phone: receipt.clientPhone,
      email: receipt.clientEmail,
      source: 'deposit',
      source_id: receipt.id,
      purchase_type: `réparation ${receipt.deviceName || receipt.deviceBrand || 'appareil'}`,
      date: receipt.createdAt?.slice(0, 10) || ''
    });
  };

  // Mettre à jour le statut de paiement
  const updateReceiptPayment = async (id, isPaid) => {
    try {
      await axios.patch(`${API}/api/deposit-receipts/${id}/payment`, 
        { isPaid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isPaid ? '✅ Marqué comme payé' : '⏳ Marqué comme non payé');
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du paiement');
    }
  };

  // Ouvrir le modal de bon d'avoir (lié à un bon d'entrée)
  const openCreditNoteModal = (receipt) => {
    setCreditNoteData({
      receipt,
      clientName: receipt.clientName || '',
      clientPhone: receipt.clientPhone || '',
      amount: receipt.estimatedPrice || '',
      reason: '',
      validity: '12 mois'
    });
    setShowCreditNoteModal(true);
  };

  // Ouvrir le modal de bon d'avoir LIBRE (sans bon d'entrée)
  const openFreeCreditNoteModal = () => {
    setCreditNoteData({
      receipt: null,
      clientName: '',
      clientPhone: '',
      amount: '',
      reason: '',
      validity: '12 mois'
    });
    setShowCreditNoteModal(true);
  };

  // Générer et imprimer le bon d'avoir
  const generateCreditNote = async () => {
    const { receipt, clientName, clientPhone, amount, reason, validity } = creditNoteData;
    
    // Validation
    if (!clientName) {
      toast.error('Veuillez entrer le nom du client');
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    const creditNote = {
      noteNumber: 'AV-' + Date.now().toString().slice(-6),
      clientName: clientName,
      clientPhone: clientPhone,
      ticketNumber: receipt?.ticketNumber || null,
      deviceName: receipt?.deviceName || null,
      amount: parseFloat(amount),
      reason: reason || 'Avoir client',
      validity,
      createdAt: new Date().toISOString()
    };

    // Essayer d'imprimer via QZ Tray
    if (qzStatus.connected) {
      const result = await printCreditNoteViaQz(creditNote, printerConfig);
      if (result.success) {
        toast.success(`Bon d'avoir imprimé ! (${result.printerUsed})`);
        setShowCreditNoteModal(false);
        return;
      } else {
        toast.warning(result.message + ' - Impression navigateur...');
      }
    }

    // Fallback: impression navigateur
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bon d'avoir - ${creditNote.noteNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Courier New', monospace; 
            font-size: 12px; 
            padding: 10px;
            max-width: 80mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .big { font-size: 16px; font-weight: bold; }
          .extra-big { font-size: 24px; font-weight: bold; }
          .separator { border-bottom: 2px dashed #000; margin: 8px 0; }
          .double-sep { border-bottom: 3px solid #000; margin: 8px 0; }
          .section { margin: 8px 0; }
          .badge { 
            border: 3px solid #000; 
            padding: 5px 15px; 
            font-size: 18px;
            font-weight: bold;
            display: inline-block;
            margin: 10px 0;
          }
          .validity {
            border: 1px solid #000;
            padding: 5px;
            margin-top: 10px;
          }
          @media print {
            body { max-width: 100%; }
          }
        </style>
      </head>
      <body>
        <div class="center">
          <div class="big">${printerConfig.shopName || 'GESTION GLOBALE'}</div>
          <div>${printerConfig.address || ''}</div>
          <div>${printerConfig.phone ? 'Tel: ' + printerConfig.phone : ''}</div>
        </div>
        <div class="separator"></div>
        
        <div class="center">
          <div class="badge">BON D'AVOIR</div>
        </div>
        
        <div class="center big">N° ${creditNote.noteNumber}</div>
        <div class="center">${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</div>
        <div class="separator"></div>
        
        <div class="section">
          <div class="bold">CLIENT</div>
          <div>${creditNote.clientName}</div>
          ${creditNote.clientPhone ? `<div>Tel: ${creditNote.clientPhone}</div>` : ''}
        </div>
        <div class="separator"></div>
        
        ${creditNote.ticketNumber ? `
          <div class="section">
            <div class="bold">REFERENCE</div>
            <div>Bon d'entrée: #${creditNote.ticketNumber}</div>
            ${creditNote.deviceName ? `<div>${creditNote.deviceName}</div>` : ''}
          </div>
          <div class="separator"></div>
        ` : ''}
        
        ${creditNote.reason ? `
          <div class="section">
            <div class="bold">MOTIF</div>
            <div>${creditNote.reason}</div>
          </div>
          <div class="separator"></div>
        ` : ''}
        
        <div class="double-sep"></div>
        <div class="center">
          <div>VALEUR DU BON</div>
          <div class="extra-big">${parseFloat(creditNote.amount).toFixed(2)} €</div>
        </div>
        <div class="double-sep"></div>
        
        <div class="center validity">
          Validité: ${creditNote.validity}
        </div>
        
        <div class="separator"></div>
        <div class="center" style="font-size: 10px;">
          Ce bon est à conserver et<br>
          à présenter lors de votre<br>
          prochaine visite.
        </div>
        
        <div class="separator"></div>
        <div class="center bold">${printerConfig.footerMessage || 'Merci de votre confiance !'}</div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    };
    
    toast.success('Bon d\'avoir généré !');
    setShowCreditNoteModal(false);
  };

  // Ouvrir le modal de notification
  const openNotifyModal = (receipt, notificationType) => {
    setShowNotifyModal({ receipt, type: notificationType });
    setNotifyEmail(receipt.clientEmail || '');
    setNotifyPhone(receipt.clientPhone || '');
    // Aucun canal pré-sélectionné par défaut (SMS facturés - envoi manuel)
    setNotifyChannels({ email: false, sms: false, whatsapp: false });
  };

  // Envoyer la notification
  const sendNotification = async () => {
    if (!showNotifyModal) return;
    
    const { receipt, type } = showNotifyModal;
    const channels = [];
    if (notifyChannels.email) channels.push('email');
    if (notifyChannels.sms) channels.push('sms');
    if (notifyChannels.whatsapp) channels.push('whatsapp');

    if (channels.length === 0) {
      toast.error('Sélectionnez au moins un canal (Email, SMS ou WhatsApp)');
      return;
    }

    if (notifyChannels.email && !notifyEmail) {
      toast.error('Veuillez saisir l\'email du client');
      return;
    }

    if ((notifyChannels.sms || notifyChannels.whatsapp) && !notifyPhone) {
      toast.error('Veuillez saisir le téléphone du client');
      return;
    }

    setSendingNotification(true);
    
    // Fermer le modal immédiatement pour une meilleure UX
    const receiptToNotify = { ...receipt };
    const typeToSend = type;
    const emailToSend = notifyEmail;
    const phoneToSend = notifyPhone;
    const channelsToSend = [...channels];
    
    setShowNotifyModal(null);
    setNotifyEmail('');
    setNotifyPhone('');
    setNotifyChannels({ email: false, sms: false, whatsapp: false });
    
    toast.loading('Envoi en cours...', { id: 'notification-sending' });
    
    try {
      const response = await axios.post(
        `${API}/api/deposit-receipts/${receiptToNotify.id}/notify`,
        {
          receipt_id: receiptToNotify.id,
          channels: channelsToSend,
          notification_type: typeToSend,
          client_email: emailToSend,
          client_phone: phoneToSend
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      toast.dismiss('notification-sending');
      
      if (response.data.success) {
        const results = response.data.results;
        if (results.email?.success) {
          toast.success(`Email envoyé à ${results.email.sent_to}`);
        } else if (results.email?.error) {
          toast.error(`Erreur email: ${results.email.error}`);
        }
        if (results.sms?.success) {
          toast.success(`SMS envoyé à ${results.sms.sent_to}`);
        } else if (results.sms?.error) {
          toast.error(`Erreur SMS: ${results.sms.error}`);
        }
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.dismiss('notification-sending');
      console.error('Notification error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Délai dépassé - vérifiez si la notification a été envoyée');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      }
    } finally {
      setSendingNotification(false);
    }
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      deviceName: '',
      deviceBrand: '',
      deviceModel: '',
      problem: '',
      lockCode: '',
      lockPattern: '',
      observations: '',
      estimatedPrice: '',
      estimatedDate: '',
      isPaid: false,
      sendNotification: false, // Désactivé par défaut (SMS facturés)
      notificationChannels: [] // Aucune notification automatique
    });
    setEditingReceipt(null);
  };

  const openEditModal = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      clientName: receipt.clientName || '',
      clientPhone: receipt.clientPhone || '',
      clientEmail: receipt.clientEmail || '',
      deviceName: receipt.deviceName || '',
      deviceBrand: receipt.deviceBrand || '',
      deviceModel: receipt.deviceModel || '',
      problem: receipt.problem || '',
      lockCode: receipt.lockCode || '',
      lockPattern: receipt.lockPattern || '',
      observations: receipt.observations || '',
      estimatedPrice: receipt.estimatedPrice || '',
      estimatedDate: receipt.estimatedDate || '',
      isPaid: receipt.isPaid || false,
      sendNotification: false,
      notificationChannels: []
    });
    setShowModal(true);
  };

  // Générer le HTML du bon pour QZ Tray
  const generateReceiptHtml = (receipt) => {
    const cfg = DEFAULT_PRINTER_CONFIG;
    const patternSVG = receipt.lockPattern && receipt.lockPattern !== 'None' ? generatePatternSVG(receipt.lockPattern, 70) : '';
    // Nettoyer lockCode (peut être "None" pour les anciens enregistrements)
    const lockCode = receipt.lockCode && receipt.lockCode !== 'None' ? receipt.lockCode : '';
    const lockPattern = receipt.lockPattern && receipt.lockPattern !== 'None' ? receipt.lockPattern : '';
    
    return `
      <html>
      <head>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: ${cfg.fontSize}px;
            font-weight: bold;
            width: 72mm;
            margin: 0;
            padding: 2mm;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; margin-bottom: 8px; border-bottom: 3px solid #000; padding-bottom: 8px; }
          .shop-name { font-size: 20px; font-weight: 900; letter-spacing: 2px; }
          .shop-location { font-size: 14px; font-weight: 900; }
          .shop-info { font-size: 11px; font-weight: bold; }
          .ticket-number { font-size: 16px; font-weight: 900; text-align: center; background: #000; color: #fff; padding: 6px; margin: 8px 0; }
          .date-time { font-size: 12px; text-align: center; font-weight: 900; margin: 6px 0; }
          .section { margin: 8px 0; padding: 6px 0; border-bottom: 2px dashed #000; }
          .section-title { font-size: 12px; font-weight: 900; margin-bottom: 5px; }
          .field { margin: 4px 0; }
          .field-label { font-size: 10px; font-weight: bold; }
          .field-value { font-size: 13px; font-weight: 900; }
          .problem-box { border: 2px solid #000; padding: 6px; margin: 4px 0; }
          .lock-section { background: #f0f0f0; padding: 6px; border: 1px solid #000; }
          .pattern-box { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
          .observations { border: 2px dashed #000; padding: 6px; margin: 4px 0; }
          .footer { text-align: center; margin-top: 10px; font-size: 10px; font-weight: bold; }
          .signature-line { border-bottom: 2px solid #000; margin: 15px 0 4px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">${cfg.shopName}</div>
          <div class="shop-location">VILLEURBANNE</div>
          <div class="shop-info">${cfg.address}, ${cfg.city}</div>
          <div class="shop-info">Tél: ${cfg.phone}</div>
        </div>
        
        <div class="ticket-number">N° ${receipt.ticketNumber}</div>
        
        <div class="date-time">
          ${new Date(receipt.createdAt).toLocaleDateString('fr-FR')} à ${new Date(receipt.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
        
        <div class="section">
          <div class="section-title">CLIENT</div>
          <div class="field">
            <div class="field-label">Nom / Prénom:</div>
            <div class="field-value">${receipt.clientName}</div>
          </div>
          <div class="field">
            <div class="field-label">Téléphone:</div>
            <div class="field-value">${receipt.clientPhone || '-'}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">APPAREIL</div>
          <div class="field-value">${receipt.deviceName}</div>
          ${receipt.deviceBrand ? `<div class="field-label">${receipt.deviceBrand} ${receipt.deviceModel || ''}</div>` : ''}
        </div>
        
        <div class="section">
          <div class="section-title">PROBLEME</div>
          <div class="problem-box">${receipt.problem}</div>
        </div>
        
        ${lockCode || lockPattern ? `
        <div class="section">
          <div class="section-title">CODE DEVERROUILLAGE</div>
          <div class="lock-section">
            ${lockCode ? `<div class="field"><div class="field-label">Code:</div><div class="field-value">${lockCode}</div></div>` : ''}
            ${lockPattern ? `<div class="pattern-box">${patternSVG}<span style="font-size:10px;">(${lockPattern.replace(/-/g, '→')})</span></div>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${receipt.observations ? `
        <div class="section">
          <div class="section-title">OBSERVATIONS</div>
          <div class="observations">${receipt.observations}</div>
        </div>
        ` : ''}
        
        ${receipt.estimatedPrice || receipt.estimatedDate ? `
        <div class="section">
          <div class="section-title">ESTIMATION</div>
          ${receipt.estimatedPrice ? `<div class="field-value">${receipt.estimatedPrice} €</div>` : ''}
          ${receipt.estimatedDate ? `<div class="field-label">Prévu: ${receipt.estimatedDate}</div>` : ''}
        </div>
        ` : ''}
        
        ${receipt.isPaid ? `
        <div style="text-align: center; margin: 10px 0; padding: 8px; background: #000; color: #fff; font-size: 16px; font-weight: 900;">
          ✓ PAYÉ
        </div>
        ` : ''}
        
        <div class="footer">
          <div class="signature-line"></div>
          <div>Signature client</div>
          <div style="margin-top:8px;">Conservez ce bon pour récupérer votre appareil</div>
        </div>
      </body>
      </html>
    `;
  };

  // Impression via QZ Tray (POS) - Même méthode que le ticket de caisse
  const printReceipt = async (receipt) => {
    const cfg = printerConfig;
    
    toast.loading('Connexion à l\'imprimante POS...', { id: 'print-receipt' });
    
    // TOUJOURS essayer QZ Tray d'abord
    const result = await printDepositReceiptViaQz(receipt, cfg, cfg.printerName);
    
    if (result.success) {
      toast.success(`✅ ${result.message}`, { id: 'print-receipt' });
      return;
    }
    
    // Si QZ Tray échoue, informer l'utilisateur et proposer le fallback
    console.log('QZ Tray result:', result);
    toast.warning(`⚠️ ${result.message}`, { id: 'print-receipt' });
    
    // Fallback: impression via fenêtre navigateur
    printReceiptFallback(receipt);
  };

  // Impression fallback (fenêtre navigateur)
  const printReceiptFallback = (receipt) => {
    const printWindow = window.open('', '_blank', 'width=400,height=700');
    
    // Nettoyer lockCode/lockPattern (peut être "None" pour les anciens enregistrements)
    const lockCode = receipt.lockCode && receipt.lockCode !== 'None' ? receipt.lockCode : '';
    const lockPattern = receipt.lockPattern && receipt.lockPattern !== 'None' ? receipt.lockPattern : '';
    
    // Générer le SVG du pattern si présent
    const patternSVG = lockPattern ? generatePatternSVG(lockPattern, 80) : '';
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bon d'entrée - ${receipt.ticketNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: 80mm auto; margin: 0; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 14px;
            font-weight: bold;
            width: 76mm;
            margin: 0 auto;
            padding: 3mm;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; margin-bottom: 10px; border-bottom: 3px solid #000; padding-bottom: 10px; }
          .shop-name { font-size: 22px; font-weight: 900; letter-spacing: 2px; }
          .shop-location { font-size: 16px; font-weight: 900; margin-top: 3px; }
          .shop-info { font-size: 12px; margin-top: 5px; font-weight: bold; }
          .ticket-number { font-size: 18px; font-weight: 900; margin: 10px 0; text-align: center; background: #000; color: #fff; padding: 8px; }
          .section { margin: 10px 0; padding: 8px 0; border-bottom: 2px dashed #000; }
          .section-title { font-size: 13px; font-weight: 900; margin-bottom: 8px; text-transform: uppercase; }
          .field { margin: 6px 0; }
          .field-label { font-size: 11px; color: #333; font-weight: bold; }
          .field-value { font-size: 14px; font-weight: 900; }
          .problem-box { border: 2px solid #000; padding: 10px; margin: 5px 0; min-height: 40px; font-weight: bold; }
          .lock-section { background: #f0f0f0; padding: 10px; margin: 5px 0; border: 1px solid #000; }
          .pattern-container { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
          .pattern-label { font-size: 12px; }
          .observations { border: 2px dashed #000; padding: 10px; margin: 5px 0; min-height: 30px; }
          .footer { text-align: center; margin-top: 15px; font-size: 11px; font-weight: bold; }
          .signature-line { border-bottom: 2px solid #000; margin: 25px 0 5px 0; }
          .date-time { font-size: 13px; text-align: center; margin: 10px 0; font-weight: 900; }
          .no-print { margin-top: 20px; }
          @media print { 
            body { width: 76mm; } 
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">PHONELAB</div>
          <div class="shop-location">VILLEURBANNE</div>
          <div class="shop-info">5 Rue Paul Verlaine, 69100</div>
          <div class="shop-info">Tél: 04 26 00 82 10</div>
        </div>
        
        <div class="ticket-number">N° ${receipt.ticketNumber}</div>
        
        <div class="date-time">
          ${new Date(receipt.createdAt).toLocaleDateString('fr-FR')} à ${new Date(receipt.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
        
        <div class="section">
          <div class="section-title">CLIENT</div>
          <div class="field">
            <div class="field-label">Nom / Prénom:</div>
            <div class="field-value">${receipt.clientName}</div>
          </div>
          <div class="field">
            <div class="field-label">Téléphone:</div>
            <div class="field-value">${receipt.clientPhone || 'Non renseigné'}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">APPAREIL</div>
          <div class="field">
            <div class="field-label">Appareil:</div>
            <div class="field-value">${receipt.deviceName}</div>
          </div>
          ${receipt.deviceBrand ? `<div class="field"><div class="field-label">Marque:</div><div class="field-value">${receipt.deviceBrand}</div></div>` : ''}
          ${receipt.deviceModel ? `<div class="field"><div class="field-label">Modèle:</div><div class="field-value">${receipt.deviceModel}</div></div>` : ''}
        </div>
        
        <div class="section">
          <div class="section-title">PROBLEME SIGNALE</div>
          <div class="problem-box">${receipt.problem}</div>
        </div>
        
        ${lockCode || lockPattern ? `
        <div class="section">
          <div class="section-title">CODE DE DEVERROUILLAGE</div>
          <div class="lock-section">
            ${lockCode ? `<div class="field"><div class="field-label">Code / Mot de passe:</div><div class="field-value">${lockCode}</div></div>` : ''}
            ${lockPattern ? `
              <div class="field">
                <div class="field-label">Schéma / Pattern:</div>
                <div class="pattern-container">
                  ${patternSVG}
                  <span class="pattern-label">(${lockPattern.replace(/-/g, ' → ')})</span>
                </div>
              </div>
            ` : ''}
          </div>
        </div>
        ` : ''}
        
        ${receipt.observations ? `
        <div class="section">
          <div class="section-title">OBSERVATIONS</div>
          <div class="observations">${receipt.observations}</div>
        </div>
        ` : ''}
        
        ${receipt.estimatedPrice || receipt.estimatedDate ? `
        <div class="section">
          <div class="section-title">ESTIMATION</div>
          ${receipt.estimatedPrice ? `<div class="field"><div class="field-label">Prix estimé:</div><div class="field-value">${receipt.estimatedPrice} €</div></div>` : ''}
          ${receipt.estimatedDate ? `<div class="field"><div class="field-label">Date prévue:</div><div class="field-value">${receipt.estimatedDate}</div></div>` : ''}
        </div>
        ` : ''}
        
        <div class="footer">
          <div class="signature-line"></div>
          <div>Signature client</div>
          <br><br>
          <div style="font-size: 10px; font-weight: bold;">
            Conservez ce bon pour récupérer votre appareil.
            <br>Présentation obligatoire lors du retrait.
          </div>
        </div>
        
        <div class="no-print" style="text-align: center;">
          <button onclick="window.print()" style="padding: 10px 25px; font-size: 14px; cursor: pointer; background: #000; color: #fff; border: none; font-weight: bold;">
            IMPRIMER
          </button>
        </div>
        
        <script>
          setTimeout(() => { window.print(); }, 500);
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
  };

  // Impression étiquette autocollante 30x50mm (Clavel CT221B)
  const printLabel = async (receipt) => {
    toast.loading('Impression de l\'étiquette...', { id: 'print-label' });
    
    // Récupérer le nom de l'imprimante d'étiquettes depuis le localStorage si disponible
    const printerSettings = JSON.parse(localStorage.getItem('printerSettings') || '{}');
    const labelPrinterName = printerSettings.labelPrinterName || localStorage.getItem('labelPrinterName') || null;
    
    const result = await printDepositLabelViaQz(receipt, labelPrinterName);
    
    if (result.success) {
      toast.success(`✅ ${result.message}`, { id: 'print-label' });
    } else {
      toast.error(`❌ ${result.message}`, { id: 'print-label' });
    }
  };

  // Gestion du choix d'impression
  const handlePrintChoice = async (receipt, choice) => {
    setShowPrintChoice(null);
    setPrintingInProgress(true);
    
    try {
      if (choice === 'both') {
        // Imprimer les deux : d'abord le bon, puis l'étiquette
        await printReceipt(receipt);
        await new Promise(resolve => setTimeout(resolve, 500)); // Petite pause entre les impressions
        await printLabel(receipt);
        toast.success('Bon + Étiquette imprimés !');
      } else if (choice === 'receipt') {
        // Imprimer uniquement le bon
        printReceipt(receipt);
      } else if (choice === 'label') {
        // Imprimer uniquement l'étiquette
        await printLabel(receipt);
      }
    } catch (error) {
      console.error('Erreur impression:', error);
      toast.error('Erreur lors de l\'impression');
    } finally {
      setPrintingInProgress(false);
    }
  };

  // Filtrer les bons - séparer actifs et archives
  // Les bons "delivered", "cancelled", "not_repairable" et "hs" sont archivés
  const archivedStatuses = ['delivered', 'cancelled', 'not_repairable', 'hs'];
  const activeReceipts = receipts.filter(r => !archivedStatuses.includes(r.status));
  const archivedReceipts = receipts.filter(r => archivedStatuses.includes(r.status));
  
  const displayedReceipts = showArchives ? archivedReceipts : activeReceipts;
  
  const filteredReceipts = displayedReceipts.filter(r => 
    r.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.deviceName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper pour les statuts
  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      repaired: 'Réparé',
      delivered: 'Livré',
      not_repairable: 'Non réparable',
      cancelled: 'Annulé',
      hs: 'HS'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-amber-500/20 text-amber-400',
      repaired: 'bg-purple-500/20 text-purple-400',
      delivered: 'bg-emerald-500/20 text-emerald-400',
      not_repairable: 'bg-red-500/20 text-red-400',
      cancelled: 'bg-gray-500/20 text-gray-400',
      hs: 'bg-red-600/20 text-red-500'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">GESTION GLOBALE - Bons d'entrée</h1>
          <nav className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <Home className="w-4 h-4 mr-2" />
              Caisse
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/invoices')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <FileText className="w-4 h-4 mr-2" />
              Facturation
            </Button>
            <Button
              variant="ghost"
              className="text-emerald-400 bg-white/10"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Bons d'entrée
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/order-alerts')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <Package className="w-4 h-4 mr-2" />
              Commandes
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/history')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <History className="w-4 h-4 mr-2" />
              Historique
            </Button>
            <div className="border-l border-white/20 h-6 mx-2"></div>
            <Button
              variant="ghost"
              onClick={() => setShowPrinterModal(true)}
              className="text-white/70 hover:text-white hover:bg-white/10"
              title="Configuration imprimante"
            >
              <Printer className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      <main className="p-6">
        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Rechercher un bon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowArchives(!showArchives)}
              variant={showArchives ? "default" : "outline"}
              className={showArchives 
                ? "bg-slate-600 hover:bg-slate-500" 
                : "border-white/20 text-white hover:bg-white/10"
              }
            >
              <Archive className="w-4 h-4 mr-2" />
              Archives ({archivedReceipts.length})
            </Button>
            <Button
              onClick={() => openFreeCreditNoteModal()}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="new-credit-note-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Nouveau Bon d'avoir
            </Button>
            <Button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau bon d'entrée
            </Button>
          </div>
        </div>

        {/* Titre section */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-white">
            {showArchives ? `Archives (${archivedReceipts.length} bons)` : `Bons actifs (${activeReceipts.length})`}
          </h2>
          <p className="text-white/50 text-sm">
            {showArchives 
              ? 'Bons livrés, annulés, non réparables ou HS - Cliquez sur "Désarchiver" pour réactiver un bon' 
              : 'Workflow: En attente → En cours → Réparé → Livré (archivé)'
            }
          </p>
        </div>

        {/* Receipts List */}
        <div className="bg-slate-800 rounded-xl border border-white/10 overflow-visible">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">N° Ticket</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Appareil</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Problème</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Statut</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-white/70">Paiement</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-white/70">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-white/50">
                    Chargement...
                  </td>
                </tr>
              ) : filteredReceipts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-white/50">
                    Aucun bon d'entrée trouvé
                  </td>
                </tr>
              ) : (
                filteredReceipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-white/5">
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => setShowPreview(receipt)}
                        className="font-mono font-bold text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer"
                      >
                        {receipt.ticketNumber}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {new Date(receipt.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{receipt.clientName}</div>
                      <div className="text-white/50 text-sm">{receipt.clientPhone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{receipt.deviceName}</div>
                      <div className="text-white/50 text-sm">{receipt.deviceBrand} {receipt.deviceModel}</div>
                    </td>
                    <td className="px-4 py-3 text-white/70 max-w-xs truncate">
                      {receipt.problem}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(receipt.status)}`}>
                        {getStatusLabel(receipt.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => updateReceiptPayment(receipt.id, !receipt.isPaid)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer ${
                          receipt.isPaid 
                            ? 'bg-emerald-500/30 text-emerald-300 border-2 border-emerald-400 font-bold shadow-lg shadow-emerald-500/20' 
                            : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30 animate-pulse font-medium'
                        }`}
                        title={receipt.isPaid ? 'Cliquer pour marquer non payé' : 'Cliquer pour marquer payé'}
                        data-testid="payment-status-btn"
                      >
                        {receipt.isPaid ? '✓ PAYÉ' : '✗ Non payé'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {/* Boutons de changement de statut positifs */}
                        {receipt.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => updateReceiptStatus(receipt.id, 'in_progress')}
                            className="bg-amber-500 hover:bg-amber-600 text-xs h-7 animate-pulse"
                            title="Marquer en cours"
                          >
                            <Wrench className="w-3 h-3 mr-1" />
                            En cours
                          </Button>
                        )}
                        {receipt.status === 'in_progress' && (
                          <Button
                            size="sm"
                            onClick={() => updateReceiptStatus(receipt.id, 'repaired')}
                            className="bg-purple-500 hover:bg-purple-600 text-xs h-7 animate-pulse"
                            title="Marquer réparé"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Réparé
                          </Button>
                        )}
                        {receipt.status === 'repaired' && (
                          <Button
                            size="sm"
                            onClick={() => updateReceiptStatus(receipt.id, 'delivered')}
                            className="bg-emerald-500 hover:bg-emerald-600 text-xs h-7 animate-pulse"
                            title="Marquer livré (archiver)"
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            Livré
                          </Button>
                        )}
                        
                        {/* Sous-menu pour statuts négatifs */}
                        {(receipt.status === 'pending' || receipt.status === 'in_progress') && (
                          <div className="relative">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowStatusSubmenu(showStatusSubmenu === receipt.id ? null : receipt.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                              title="Autres statuts"
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                            {showStatusSubmenu === receipt.id && (
                              <div className="absolute right-0 top-8 z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[140px]">
                                <button
                                  onClick={() => { updateReceiptStatus(receipt.id, 'not_repairable'); setShowStatusSubmenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/20 flex items-center gap-2"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Non réparable
                                </button>
                                <button
                                  onClick={() => { updateReceiptStatus(receipt.id, 'cancelled'); setShowStatusSubmenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm text-gray-400 hover:bg-gray-500/20 flex items-center gap-2"
                                >
                                  <X className="w-4 h-4" />
                                  Annulé
                                </button>
                                <button
                                  onClick={() => { updateReceiptStatus(receipt.id, 'hs'); setShowStatusSubmenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-500/20 flex items-center gap-2"
                                >
                                  <AlertTriangle className="w-4 h-4" />
                                  HS
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Bouton Notifier - Change de couleur si déjà notifié */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openNotifyModal(receipt, 
                            receipt.status === 'repaired' ? 'repaired' : 
                            receipt.status === 'delivered' ? 'delivered' :
                            receipt.status === 'not_repairable' ? 'not_repairable' :
                            receipt.status === 'hs' ? 'hs' :
                            receipt.status === 'cancelled' ? 'cancelled' : 'in_progress'
                          )}
                          className={`h-7 px-2 ${
                            receipt.clientNotified 
                              ? 'text-emerald-400 bg-emerald-500/20 hover:bg-emerald-500/30' 
                              : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
                          }`}
                          title={receipt.clientNotified ? 'Client déjà notifié - Renvoyer' : 'Notifier le client'}
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          <span className="text-xs">{receipt.clientNotified ? '✓ Notifié' : 'Notifier'}</span>
                        </Button>
                        
                        {/* Bouton Désarchiver - affiché uniquement pour les bons archivés */}
                        {showArchives && ['delivered', 'cancelled', 'not_repairable', 'hs'].includes(receipt.status) && (
                          <Button
                            size="sm"
                            onClick={() => unarchiveReceipt(receipt.id)}
                            className="bg-amber-500 hover:bg-amber-600 text-white h-7 px-2 text-xs"
                            title="Désarchiver et remettre en cours"
                            data-testid="unarchive-btn"
                          >
                            <Wrench className="w-3 h-3 mr-1" />
                            Désarchiver
                          </Button>
                        )}
                        
                        {/* Bouton Demander Avis - affiché pour les bons archivés livrés */}
                        {showArchives && receipt.status === 'delivered' && (receipt.clientPhone || receipt.clientEmail) && (
                          <Button
                            size="sm"
                            onClick={() => openReviewModal(receipt)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white h-7 px-2 text-xs"
                            title="Demander un avis client"
                            data-testid="quick-review-btn"
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Avis
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowPreview(receipt)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* Bouton Impression - ouvre modal de choix */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowPrintChoice(receipt)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Imprimer"
                          data-testid="print-menu-btn"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditModal(receipt)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(receipt.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      </main>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/20">
            <div className="sticky top-0 bg-slate-800 px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingReceipt ? 'Modifier le bon' : 'Nouveau bon d\'entrée'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Client Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Informations client
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm text-white/70 mb-1">Nom / Prénom *</label>
                    <Input
                      value={formData.clientName}
                      onChange={(e) => {
                        setFormData({...formData, clientName: e.target.value});
                        searchExistingClients(e.target.value, 'name');
                      }}
                      onFocus={() => formData.clientName.length >= 2 && searchExistingClients(formData.clientName, 'name')}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      placeholder="Jean Dupont"
                      className="bg-white/10 border-white/20 text-white"
                      required
                      data-testid="deposit-client-name-input"
                    />
                    {showClientSuggestions && clientSuggestions.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-slate-800 border border-white/20 rounded-lg shadow-xl max-h-56 overflow-auto">
                        <div className="px-3 py-1.5 text-xs text-white/50 border-b border-white/10">
                          {clientSuggestions.length} fiche(s) client existante(s)
                        </div>
                        {clientSuggestions.map((c, idx) => (
                          <button
                            key={c.id || idx}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); selectExistingClient(c); }}
                            className="w-full text-left px-3 py-2 hover:bg-white/10 border-b border-white/5 last:border-0"
                            data-testid={`deposit-client-suggestion-${idx}`}
                          >
                            <div className="text-white text-sm font-medium">{c.name || '(sans nom)'}</div>
                            <div className="text-white/50 text-xs">
                              {c.phone && <span className="mr-2">{c.phone}</span>}
                              {c.email && <span>{c.email}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Téléphone</label>
                    <Input
                      value={formData.clientPhone}
                      onChange={(e) => {
                        setFormData({...formData, clientPhone: e.target.value});
                        searchExistingClients(e.target.value, 'phone');
                      }}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      placeholder="06 12 34 56 78"
                      className="bg-white/10 border-white/20 text-white"
                      data-testid="deposit-client-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData.clientEmail}
                      onChange={(e) => {
                        setFormData({...formData, clientEmail: e.target.value});
                        searchExistingClients(e.target.value, 'email');
                      }}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      placeholder="client@email.com"
                      className="bg-white/10 border-white/20 text-white"
                      data-testid="deposit-client-email-input"
                    />
                  </div>
                </div>
                
                {/* Options de notification à la création */}
                {!editingReceipt && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-400">Notification automatique</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.sendNotification}
                          onChange={(e) => setFormData({...formData, sendNotification: e.target.checked})}
                          className="w-4 h-4 rounded border-white/30 bg-white/10"
                        />
                        <span className="text-sm text-white/70">Envoyer une copie du bon au client</span>
                      </label>
                    </div>
                    {formData.sendNotification && (
                      <div className="flex items-center gap-4 mt-3 ml-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notificationChannels.includes('email')}
                            onChange={(e) => {
                              const channels = e.target.checked 
                                ? [...formData.notificationChannels, 'email']
                                : formData.notificationChannels.filter(c => c !== 'email');
                              setFormData({...formData, notificationChannels: channels});
                            }}
                            className="w-4 h-4 rounded border-white/30 bg-white/10"
                          />
                          <Mail className="w-4 h-4 text-white/50" />
                          <span className="text-sm text-white/70">Email</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.notificationChannels.includes('sms')}
                            onChange={(e) => {
                              const channels = e.target.checked 
                                ? [...formData.notificationChannels, 'sms']
                                : formData.notificationChannels.filter(c => c !== 'sms');
                              setFormData({...formData, notificationChannels: channels});
                            }}
                            className="w-4 h-4 rounded border-white/30 bg-white/10"
                          />
                          <MessageSquare className="w-4 h-4 text-white/50" />
                          <span className="text-sm text-white/70">SMS</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Device Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Informations appareil
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Appareil *</label>
                    <Input
                      value={formData.deviceName}
                      onChange={(e) => setFormData({...formData, deviceName: e.target.value})}
                      placeholder="iPhone 14 Pro"
                      className="bg-white/10 border-white/20 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Marque</label>
                    <Input
                      value={formData.deviceBrand}
                      onChange={(e) => setFormData({...formData, deviceBrand: e.target.value})}
                      placeholder="Apple"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Modèle / IMEI</label>
                    <Input
                      value={formData.deviceModel}
                      onChange={(e) => setFormData({...formData, deviceModel: e.target.value})}
                      placeholder="A2890"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Problem */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Problème signalé *
                </h3>
                <textarea
                  value={formData.problem}
                  onChange={(e) => setFormData({...formData, problem: e.target.value})}
                  placeholder="Décrivez le problème..."
                  rows={3}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/40"
                  required
                />
              </div>

              {/* Lock Code */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Code de déverrouillage
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Code PIN / Mot de passe</label>
                    <Input
                      value={formData.lockCode}
                      onChange={(e) => setFormData({...formData, lockCode: e.target.value})}
                      placeholder="1234 ou motdepasse"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-3">Schéma / Pattern</label>
                    <PatternLockInput
                      value={formData.lockPattern}
                      onChange={(pattern) => setFormData({...formData, lockPattern: pattern})}
                      size={180}
                    />
                  </div>
                </div>
              </div>

              {/* Observations */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Observations
                </h3>
                <textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({...formData, observations: e.target.value})}
                  placeholder="Rayures, état de la coque, accessoires déposés..."
                  rows={2}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/40"
                />
              </div>

              {/* Estimation */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Estimation (optionnel)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Prix estimé (€)</label>
                    <Input
                      type="number"
                      value={formData.estimatedPrice}
                      onChange={(e) => setFormData({...formData, estimatedPrice: e.target.value})}
                      placeholder="50"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Date prévue</label>
                    <Input
                      type="date"
                      value={formData.estimatedDate}
                      onChange={(e) => setFormData({...formData, estimatedDate: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
                
                {/* Statut de paiement */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white/80">Statut de paiement</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, isPaid: !formData.isPaid})}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                      formData.isPaid 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/50'
                    }`}
                  >
                    {formData.isPaid ? '✓ Payé' : '✗ Non payé'}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingReceipt ? 'Modifier' : 'Créer le bon'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation détaillée */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Bon d'entrée #{showPreview.ticketNumber}
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => setShowPreview(null)}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Informations client */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Client
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-white/50 text-xs">Nom</p>
                      <p className="text-white font-medium">{showPreview.clientName}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Téléphone</p>
                      <p className="text-white font-medium">{showPreview.clientPhone}</p>
                    </div>
                  </div>
                </div>

                {/* Informations appareil */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Appareil
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/50 text-xs">Type</p>
                      <p className="text-white font-medium">{showPreview.deviceName}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Marque</p>
                      <p className="text-white font-medium">{showPreview.deviceBrand}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Modèle</p>
                      <p className="text-white font-medium">{showPreview.deviceModel || '-'}</p>
                    </div>
                    {showPreview.imei && (
                      <div>
                        <p className="text-white/50 text-xs">IMEI</p>
                        <p className="text-white font-mono text-sm">{showPreview.imei}</p>
                      </div>
                    )}
                    {showPreview.serialNumber && (
                      <div>
                        <p className="text-white/50 text-xs">N° Série</p>
                        <p className="text-white font-mono text-sm">{showPreview.serialNumber}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Problème */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Problème signalé
                  </h3>
                  <p className="text-white">{showPreview.problem}</p>
                </div>

                {/* Accessoires */}
                {showPreview.accessories && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-400 mb-3">Accessoires inclus</h3>
                    <p className="text-white">{showPreview.accessories}</p>
                  </div>
                )}

                {/* Pattern de déverrouillage */}
                {showPreview.lockPattern && showPreview.lockPattern !== 'None' && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Schéma de déverrouillage
                    </h3>
                    <div className="flex justify-center">
                      <PatternLockInput 
                        value={showPreview.lockPattern} 
                        onChange={() => {}} 
                        readOnly 
                        size={150}
                      />
                    </div>
                  </div>
                )}

                {/* Code PIN / Code de déverrouillage */}
                {showPreview.lockCode && showPreview.lockCode !== 'None' && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Code PIN / Mot de passe
                    </h3>
                    <p className="text-white font-mono text-2xl tracking-widest">{showPreview.lockCode}</p>
                  </div>
                )}

                {/* Estimation */}
                {(showPreview.estimatedPrice || showPreview.estimatedDate) && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Estimation
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {showPreview.estimatedPrice && (
                        <div>
                          <p className="text-white/50 text-xs">Prix estimé</p>
                          <p className="text-white font-bold text-xl">{showPreview.estimatedPrice} €</p>
                        </div>
                      )}
                      {showPreview.estimatedDate && (
                        <div>
                          <p className="text-white/50 text-xs">Date prévue</p>
                          <p className="text-white">{new Date(showPreview.estimatedDate).toLocaleDateString('fr-FR')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Observations */}
                {showPreview.observations && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-white/70 mb-3">Observations</h3>
                    <p className="text-white">{showPreview.observations}</p>
                  </div>
                )}

                {/* Métadonnées */}
                <div className="text-white/50 text-sm flex items-center justify-between pt-4 border-t border-white/10">
                  <span>Créé le {new Date(showPreview.createdAt).toLocaleString('fr-FR')}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    showPreview.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                    showPreview.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {showPreview.status === 'completed' ? 'Terminé' :
                     showPreview.status === 'in_progress' ? 'En cours' : 'En attente'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(null)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Fermer
                </Button>
                <Button
                  onClick={() => printReceipt(showPreview)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuration imprimante */}
      {showPrinterModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-white/10 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Printer className="w-5 h-5" />
                  Configuration Imprimante
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => setShowPrinterModal(false)}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Statut QZ Tray */}
              <div className={`p-3 rounded-lg mb-4 flex items-center gap-3 ${
                qzStatus.connected ? 'bg-emerald-500/20' : 'bg-red-500/20'
              }`}>
                {qzStatus.connected ? (
                  <Wifi className="w-5 h-5 text-emerald-400" />
                ) : (
                  <WifiOff className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <p className={`font-medium ${qzStatus.connected ? 'text-emerald-400' : 'text-red-400'}`}>
                    {qzStatus.connected ? 'QZ Tray connecté' : 'QZ Tray non connecté'}
                  </p>
                  <p className="text-white/50 text-sm">
                    {qzStatus.connected 
                      ? `${availablePrinters.length} imprimante(s) détectée(s)`
                      : 'Installez QZ Tray pour l\'impression directe'
                    }
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Toggle QZ Tray */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Impression directe (QZ Tray)</p>
                    <p className="text-white/50 text-sm">Imprimer directement sur l'imprimante POS</p>
                  </div>
                  <Button
                    variant={printerConfig.useQzTray ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const newConfig = {...printerConfig, useQzTray: !printerConfig.useQzTray};
                      setPrinterConfig(newConfig);
                      localStorage.setItem('printerConfig', JSON.stringify(newConfig));
                    }}
                    className={printerConfig.useQzTray ? "bg-emerald-500 hover:bg-emerald-600" : "border-white/20 text-white"}
                  >
                    {printerConfig.useQzTray ? 'Activé' : 'Désactivé'}
                  </Button>
                </div>

                {/* Sélection imprimante */}
                {printerConfig.useQzTray && availablePrinters.length > 0 && (
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Imprimante</label>
                    <select
                      value={printerConfig.printerName || ''}
                      onChange={(e) => {
                        const newConfig = {...printerConfig, printerName: e.target.value};
                        setPrinterConfig(newConfig);
                        localStorage.setItem('printerConfig', JSON.stringify(newConfig));
                      }}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">Sélectionner...</option>
                      {availablePrinters.map((p, i) => (
                        <option key={i} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Largeur ticket */}
                <div>
                  <label className="block text-sm text-white/70 mb-2">Largeur du ticket (mm)</label>
                  <select
                    value={printerConfig.ticketWidth || 80}
                    onChange={(e) => {
                      const newConfig = {...printerConfig, ticketWidth: parseInt(e.target.value)};
                      setPrinterConfig(newConfig);
                      localStorage.setItem('printerConfig', JSON.stringify(newConfig));
                    }}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                  >
                    <option value={58}>58 mm</option>
                    <option value={80}>80 mm (standard)</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={async () => {
                    const status = await getQzStatus();
                    setQzStatus(status);
                    if (status.connected) {
                      setAvailablePrinters(status.printers);
                      toast.success(`${status.printers.length} imprimante(s) détectée(s)`);
                    } else {
                      toast.error('QZ Tray non connecté');
                    }
                  }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Rafraîchir
                </Button>
                <Button
                  onClick={() => setShowPrinterModal(false)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de notification */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-md w-full border border-white/20">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-400" />
                Notifier le client
              </h2>
              <button onClick={() => setShowNotifyModal(null)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Info du bon */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-sm text-white/50">Ticket N°</div>
                <div className="text-white font-medium">{showNotifyModal.receipt.ticketNumber}</div>
                <div className="text-sm text-white/50 mt-2">Client</div>
                <div className="text-white">{showNotifyModal.receipt.clientName}</div>
              </div>

              {/* Type de notification */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="text-sm text-blue-400 font-medium">
                  {showNotifyModal.type === 'creation' && "📝 Confirmation de dépôt"}
                  {showNotifyModal.type === 'in_progress' && "🔧 Réparation en cours"}
                  {showNotifyModal.type === 'repaired' && "✅ Appareil prêt !"}
                  {showNotifyModal.type === 'delivered' && "🎉 Appareil récupéré"}
                </div>
              </div>

              {/* Canaux de notification */}
              <div className="space-y-3">
                <div className="text-sm text-white/70 font-medium">Envoyer par :</div>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.email}
                    onChange={(e) => setNotifyChannels({...notifyChannels, email: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    <div className="text-white text-sm">Email</div>
                    {notifyChannels.email && (
                      <Input
                        type="email"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="client@email.com"
                        className="mt-2 bg-white/10 border-white/20 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.sms}
                    onChange={(e) => setNotifyChannels({...notifyChannels, sms: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  <div className="flex-1">
                    <div className="text-white text-sm">SMS</div>
                    {notifyChannels.sms && (
                      <Input
                        value={notifyPhone}
                        onChange={(e) => setNotifyPhone(e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-2 bg-white/10 border-white/20 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 cursor-pointer hover:bg-green-500/10 bg-green-500/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.whatsapp}
                    onChange={(e) => setNotifyChannels({...notifyChannels, whatsapp: e.target.checked})}
                    className="w-4 h-4 rounded accent-green-500"
                  />
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  <div className="flex-1">
                    <div className="text-green-400 text-sm font-medium">WhatsApp</div>
                    {notifyChannels.whatsapp && (
                      <Input
                        value={notifyPhone}
                        onChange={(e) => setNotifyPhone(e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-2 bg-white/10 border-green-500/30 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowNotifyModal(null)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={sendingNotification}
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendNotification}
                  disabled={sendingNotification || (!notifyChannels.email && !notifyChannels.sms && !notifyChannels.whatsapp)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                >
                  {sendingNotification ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal choix d'impression */}
      {showPrintChoice && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowPrintChoice(null)}
        >
          <div 
            className="bg-slate-800 rounded-xl border border-white/20 p-6 min-w-[320px] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
              <Printer className="w-5 h-5 text-emerald-400" />
              Options d'impression
            </h3>
            <p className="text-white/50 text-sm mb-4">
              Bon #{showPrintChoice.ticketNumber} - {showPrintChoice.clientName}
            </p>
            
            <div className="space-y-2">
              <button
                onClick={() => handlePrintChoice(showPrintChoice, 'both')}
                disabled={printingInProgress}
                className="w-full px-4 py-3 text-left text-white bg-gradient-to-r from-emerald-600 to-amber-600 hover:from-emerald-500 hover:to-amber-500 rounded-lg flex items-center gap-3 transition-all"
              >
                <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-1">
                  <Printer className="w-4 h-4" />
                  <span className="text-xs font-bold">+</span>
                  <Tag className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold">Bon + Étiquette</div>
                  <div className="text-xs text-white/70">Imprimer les deux documents</div>
                </div>
              </button>
              
              <button
                onClick={() => handlePrintChoice(showPrintChoice, 'receipt')}
                disabled={printingInProgress}
                className="w-full px-4 py-3 text-left text-white bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-3 transition-all"
              >
                <div className="bg-emerald-500/20 rounded-full p-2">
                  <Printer className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <div className="font-semibold">Bon seul</div>
                  <div className="text-xs text-white/50">Ticket thermique complet</div>
                </div>
              </button>
              
              <button
                onClick={() => handlePrintChoice(showPrintChoice, 'label')}
                disabled={printingInProgress}
                className="w-full px-4 py-3 text-left text-white bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-3 transition-all"
              >
                <div className="bg-amber-500/20 rounded-full p-2">
                  <Tag className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <div className="font-semibold text-amber-400">Étiquette seule</div>
                  <div className="text-xs text-white/50">Autocollant 30×50mm</div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowPrintChoice(null)}
              className="w-full mt-4 py-2 text-center text-white/50 hover:text-white text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Modal Bon d'Avoir */}
      {showCreditNoteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="credit-note-modal">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-400" />
                {creditNoteData.receipt ? 'Bon d\'Avoir (lié)' : 'Nouveau Bon d\'Avoir'}
              </h2>
              <button 
                onClick={() => setShowCreditNoteModal(false)} 
                className="text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Si lié à un bon d'entrée, afficher les infos */}
            {creditNoteData.receipt ? (
              <div className="mb-4 p-3 rounded-lg bg-slate-700/50 border border-white/10">
                <div className="text-sm text-white/70">Client</div>
                <div className="text-white font-bold">{creditNoteData.receipt.clientName}</div>
                <div className="text-white/50 text-sm">{creditNoteData.receipt.clientPhone}</div>
                <div className="mt-2 text-xs text-white/50">
                  Réf: #{creditNoteData.receipt.ticketNumber} - {creditNoteData.receipt.deviceName}
                </div>
              </div>
            ) : (
              /* Sinon, champs de saisie pour le client */
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">
                    Nom du client *
                  </label>
                  <Input
                    type="text"
                    value={creditNoteData.clientName}
                    onChange={(e) => setCreditNoteData({...creditNoteData, clientName: e.target.value})}
                    placeholder="Nom du client"
                    className="bg-white/10 border-white/20 text-white"
                    data-testid="credit-note-client-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">
                    Téléphone (optionnel)
                  </label>
                  <Input
                    type="tel"
                    value={creditNoteData.clientPhone}
                    onChange={(e) => setCreditNoteData({...creditNoteData, clientPhone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
            )}

            {/* Montant */}
            <div className="mb-4">
              <label className="block text-sm text-white/70 mb-1">
                Montant du bon d'avoir (€) *
              </label>
              <Input
                type="number"
                value={creditNoteData.amount}
                onChange={(e) => setCreditNoteData({...creditNoteData, amount: e.target.value})}
                placeholder="0.00"
                className="bg-white/10 border-amber-500/50 text-white text-xl font-bold text-center"
                data-testid="credit-note-amount"
              />
            </div>

            {/* Motif */}
            <div className="mb-4">
              <label className="block text-sm text-white/70 mb-1">
                Motif (optionnel)
              </label>
              <Input
                type="text"
                value={creditNoteData.reason}
                onChange={(e) => setCreditNoteData({...creditNoteData, reason: e.target.value})}
                placeholder="Remboursement, geste commercial, réparation..."
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            {/* Validité */}
            <div className="mb-6">
              <label className="block text-sm text-white/70 mb-1">
                Validité
              </label>
              <select
                value={creditNoteData.validity}
                onChange={(e) => setCreditNoteData({...creditNoteData, validity: e.target.value})}
                className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
              >
                <option value="3 mois" className="bg-slate-800">3 mois</option>
                <option value="6 mois" className="bg-slate-800">6 mois</option>
                <option value="12 mois" className="bg-slate-800">12 mois</option>
                <option value="Illimitée" className="bg-slate-800">Illimitée</option>
              </select>
            </div>

            {/* Boutons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowCreditNoteModal(false)} 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
              <Button 
                onClick={generateCreditNote}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                data-testid="print-credit-note-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Demande d'Avis */}
      <ReviewRequestModal
        isOpen={!!showReviewModal}
        onClose={() => setShowReviewModal(null)}
        client={showReviewModal}
        token={token}
        onSuccess={() => toast.success('Avis demandé avec succès')}
      />
    </div>
  );
}
